import { CustomErrorBoundary } from '@omnea-app/fe-legacy/utils/error-boundary/CustomErrorBoundary'
import { NotFoundBoundary } from '@omnea-app/fe-legacy/utils/error-boundary/NotFoundBoundary'
import { FullWidthLoadingFallback } from '@omnea-app/fe-render-boundaries/LoadingFallbacks'
import { Redirect } from '@omnea-app/fe-routing/Redirect'
import { readRecordId } from '@omnea-app/fe-routing/routeParams'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { useFullAuthzContext } from './AuthzContextProvider'
import { RecordAuthorizationProvider } from './RecordAuthorizationProvider'
import { resolveAccess } from './resolveAccess'
import { resolveRouteKey } from './resolveRouteKey'
import { hrefForFrom } from './routeHref'
import { DEFAULT_DENIED_REDIRECT, ROUTE_REGISTRY, type RouteDefinition } from './routeRegistry'
import { useAccessSources } from './useAccessSources'
import { useRouteAccess } from './useRouteAccess'
import type { AccessAnswer } from './accessRules'
import type { AskedRecord } from './askedRecord'
import type { RecordPermissionsEntity } from '@omnea-digital/authz-model'
import type { ParsedUrlQuery } from 'querystring'
import type { PropsWithChildren } from 'react'

export type GatedRoute = { pattern: string; query: ParsedUrlQuery; isQueryResolved: boolean }

type RouteAccessGateProps = PropsWithChildren<{ route: GatedRoute }>

/**
 * Withholds a route the caller may not reach, and then the record it addresses. Mounted only for
 * authenticated callers, since `useRouteAccess` reads a context the unauthenticated tree never mounts.
 */
export const RouteAccessGate = ({ children, route }: RouteAccessGateProps) => {
  const access = useRouteAccess()
  const authzContext = useFullAuthzContext()

  // Next leaves the query empty until hydration, and a key discriminated by a query segment would
  // resolve to nothing — rendering the route ungated rather than withheld.
  const routeKey = route.isQueryResolved ? resolveRouteKey(route.pattern, route.query) : null
  const definition: RouteDefinition | undefined = routeKey == null ? undefined : ROUTE_REGISTRY[routeKey]
  // Every `RouteKey` is answered, so a miss is impossible; withhold rather than deny if one ever is.
  const routeAnswer: AccessAnswer = routeKey == null ? 'pending' : (access.get(routeKey) ?? 'pending')

  // Asked only once the route has allowed and the address is one the caller will stay on, so a route
  // about to redirect issues no request for a record they will never reach.
  const recordGate =
    definition?.record != null && routeAnswer === 'allowed' && definition.redirect?.when !== 'always'
      ? { rule: definition.record, entity: definition.record.authorization.entity, recordId: readRecordId(route.query) }
      : null
  const asked: AskedRecord<RecordPermissionsEntity> | null =
    recordGate == null ? null : { entity: recordGate.entity, recordId: recordGate.recordId }
  const sources = useAccessSources(asked ?? undefined)

  if (route.isQueryResolved === false) return <FullWidthLoadingFallback />
  // A route the registry does not name stays reachable, so coverage can grow without a missing key
  // becoming a silent outage.
  if (definition == null) return <>{children}</>
  // Before the answer, since the answer for the address as typed is not the answer for the page it names.
  if (definition.redirect?.when === 'always') {
    return <Redirect to={hrefForFrom(definition.redirect.to, route.query)} type="replace" keepFragment />
  }

  switch (routeAnswer) {
    case 'unavailable':
      return <CustomErrorBoundary errorData={{ error: authzContext.status === 'unavailable' ? authzContext.error : undefined }} />
    case 'pending':
      return <FullWidthLoadingFallback />
    case 'denied':
      return <Redirect to={hrefForFrom(definition.redirect?.to ?? DEFAULT_DENIED_REDIRECT, route.query)} type="replace" />
    case 'allowed':
      break
    default:
      return assertNever(routeAnswer)
  }

  if (recordGate == null) return <>{children}</>

  // A missing record answer reaches the resolver's throw rather than rendering the page ungated.
  const { record } = sources
  const recordAnswer = resolveAccess(recordGate.rule, sources)

  switch (recordAnswer) {
    case 'unavailable':
      return (
        <CustomErrorBoundary
          errorData={{ error: record?.status === 'unavailable' ? record.error : undefined }}
          resourceType={recordGate.entity}
        />
      )
    case 'pending':
      return <FullWidthLoadingFallback />
    // Not-found, not forbidden: a denied caller must not learn that the record exists.
    case 'denied':
      return <NotFoundBoundary />
    case 'allowed':
      return (
        <RecordAuthorizationProvider entity={recordGate.entity} recordId={recordGate.recordId} authorization={record ?? null}>
          {children}
        </RecordAuthorizationProvider>
      )
    default:
      return assertNever(recordAnswer)
  }
}
